import React, { createContext, useContext, useEffect, useState } from "react";
import Loading from "../components/Loading";
import { getCookie, setCookie } from "../utilities/cookies";
import { handleAxiosError } from "../utilities/function";
import { useMessage } from "../components/Header";
import { validateCentralSession, clearLocalIdentity } from "../identity/identitySession";
import { createSessionLifecycle, SESSION_STATE } from "../identity/sessionLifecycle";
import { consumeIdentityDestination } from "../identity/identityClient";

const authorizeContext = createContext();
const CALLBACK_KEYS = ["userId", "refreshToken", "accessToken", "sessionToken", "securityLevel"];

function cleanToken(value) {
  if (!value || value === "undefined" || value === "null") return "";
  return String(value).replace(/^"|"$/g, "").trim();
}

function safeStoredUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

function hasIdentityCallbackInLocation() {
  const url = new URL(window.location.href);
  if (url.hash.startsWith("#identity_callback?")) return true;
  return CALLBACK_KEYS.some((key) => url.searchParams.has(key));
}

function captureIdentityCallback() {
  const url = new URL(window.location.href);
  const fragment = url.hash.startsWith("#identity_callback?")
    ? new URLSearchParams(url.hash.slice("#identity_callback?".length))
    : new URLSearchParams();
  const readCallback = (key) => url.searchParams.get(key) || fragment.get(key) || "";
  const queryUserId = readCallback("userId");
  const refreshToken = cleanToken(readCallback("refreshToken"));
  const accessToken = cleanToken(readCallback("accessToken"));
  const sessionToken = cleanToken(readCallback("sessionToken"));
  const hadSensitiveCallback = CALLBACK_KEYS.some((key) => url.searchParams.has(key) || fragment.has(key));

  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
    setCookie("refreshToken", refreshToken);
  }
  if (accessToken) {
    localStorage.setItem("token", accessToken);
    setCookie("clikkleStoreAccessToken", accessToken);
  }
  if (sessionToken) localStorage.setItem("sessionToken", sessionToken);
  if (/^[0-9a-fA-F]{24}$/.test(queryUserId)) setCookie("userId", queryUserId);

  // Callback credentials must not remain in browser history, screenshots,
  // copied URLs, referrers or subsequent navigation. Preserve commerce fields.
  if (hadSensitiveCallback) {
    CALLBACK_KEYS.forEach((key) => url.searchParams.delete(key));
    if (url.hash.startsWith("#identity_callback?")) url.hash = "";
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  return {
    userId: /^[0-9a-fA-F]{24}$/.test(queryUserId) ? queryUserId : "",
    refreshToken,
    hadSensitiveCallback,
  };
}

const AuthorizationProvider = ({ children }) => {
  const [content, setContent] = useState(<Loading message="Please wait, logging you in..." />);
  const [user, setUser] = useState({});
  const { showError } = useMessage();

  const authorize = (loggedIn, cb) => {
    if (loggedIn) {
      setContent(children);
    } else {
      setContent(<Loading message="Please wait, redirecting you to Login page..." redirectTo={`/login${window.location.search}`} />);
    }
    if (typeof cb === "function") cb(setUser);
  };

  const restoreIdentityDestination = () => {
    const destination = consumeIdentityDestination("");
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!destination || destination === "/" || destination === current) return false;
    window.location.replace(destination);
    return true;
  };

  useEffect(() => {
    const lifecycle = createSessionLifecycle({
      verify: validateCentralSession,
      channelName: "clikkle:store-auth",
      onInactive: () => {
        clearLocalIdentity();
        // Preserve current non-auth route/commerce query; /login will hand off to Accounts.
        if (window.location.pathname !== "/login" && window.location.pathname !== "/signup") {
          window.location.replace(`/login${window.location.search}`);
        }
      },
    });
    // Do not race a newly returned callback against an older local token. The
    // bootstrap effect consumes the callback first and validates the new session.
    if (localStorage.getItem("refreshToken") && !hasIdentityCallbackInLocation()) void lifecycle.check({ force: true });
    const onUnauthorized = () => void lifecycle.check({ force: true });
    window.addEventListener("clikkle:auth-unauthorized", onUnauthorized);
    return () => {
      window.removeEventListener("clikkle:auth-unauthorized", onUnauthorized);
      lifecycle.dispose();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const callback = captureIdentityCallback();
        const queryParameters = new URLSearchParams(window.location.search);
        const storedUser = safeStoredUser();
        let userId = callback.userId || storedUser?._id || getCookie("userId") || "";
        const refreshToken = cleanToken(callback.refreshToken || localStorage.getItem("refreshToken") || getCookie("refreshToken"));

        if (!refreshToken) {
          if (!cancelled) authorize(false);
          return;
        }

        // Store authorization is based on the central Accounts session. A failure
        // in a Store-local API bootstrap must never send a valid identity into a loop.
        const centralState = await validateCentralSession();
        if (centralState === SESSION_STATE.INACTIVE) {
          clearLocalIdentity();
          if (!cancelled) authorize(false);
          return;
        }
        if (centralState === SESSION_STATE.UNVERIFIABLE) {
          if (!cancelled) setContent(<Loading message="Clikkle Identity is temporarily unavailable. Retrying securely..." />);
          return;
        }

        let resolvedUser = storedUser;
        if (!userId && resolvedUser?._id) userId = resolvedUser._id;

        if (!resolvedUser || (userId && resolvedUser._id !== userId)) {
          if (!userId) {
            if (!cancelled) authorize(false);
            return;
          }
          const formData = new FormData();
          formData.append("id", userId);
          const response = await fetch("https://api.accounts.clikkle.com/api/auth/get_user_profile", { method: "POST", body: formData });
          if (!response.ok) {
            if (!cancelled) authorize(false);
            return;
          }
          const data = await response.json();
          resolvedUser = data?.user;
          if (!resolvedUser?._id) {
            if (!cancelled) authorize(false);
            return;
          }
        }

        resolvedUser = { ...resolvedUser, refreshToken };
        localStorage.setItem("refreshToken", refreshToken);
        setCookie("refreshToken", refreshToken);
        setCookie("userId", resolvedUser._id);
        localStorage.setItem("user", JSON.stringify(resolvedUser));

        if (cancelled) return;
        setUser(resolvedUser);
        authorize(true);

        if (restoreIdentityDestination()) return;

        const planId = queryParameters.get("planId");
        if (planId && window.location.pathname !== "/setup-flow" && window.location.pathname !== "/offer-payment") {
          window.location.replace(`/setup-flow${window.location.search}`);
        }
      } catch (err) {
        console.error("Store identity bootstrap failed", err);
        handleAxiosError(err, showError);
        if (!cancelled) authorize(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <authorizeContext.Provider value={{ authorize, setUser, user, setContent }}>
      {content}
    </authorizeContext.Provider>
  );
};

const useAuthorize = () => useContext(authorizeContext).authorize;
const useUser = () => useContext(authorizeContext)?.user;
const useSetUser = () => useContext(authorizeContext).setUser;
const useSetContent = () => useContext(authorizeContext).setContent;

export default AuthorizationProvider;
export { useAuthorize, useUser, useSetUser, useSetContent };
